import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  PaymentElement,
  useElements,
  useStripe,
  LinkAuthenticationElement,
} from '@stripe/react-stripe-js';
import { useAnalytics } from 'analytics';
import _ from 'lodash';
import { Col, message, Row } from 'antd';

import { completeZeroPurchase, updateCustomer } from 'api/order_sdk';
import { IGuestCartDetail, IGuestCheckoutStoreSummary } from 'api/guest_sdk';

import Agreement from 'components/Store/NewCheckout/Agreement';
import { reverse } from 'utils/urls';
import { URLS } from 'components/Routes/urls';

import { Button, Card, FormControl, TextField, Typography } from 'ui-kit';
import { getBaseUrl, scrollToTop } from 'utils/utilsV2';
import { ECheckoutFieldTypes, phoneRegExp } from './constants';
import './styles.scss';

export interface ICheckoutForm {
  storeId: number;
  customerId: string;
  order: IGuestCartDetail | IGuestCheckoutStoreSummary;
}

const CheckoutForm = ({ storeId, order, customerId }: ICheckoutForm) => {
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const capture = useAnalytics();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');

  const [errors, setErrors] = useState({
    [ECheckoutFieldTypes.FIRST_NAME]: '',
    [ECheckoutFieldTypes.LAST_NAME]: '',
    [ECheckoutFieldTypes.PHONE_NUMBER]: '',
    [ECheckoutFieldTypes.EMAIL]: '',
  });

  const orderIsForZeroAmount = useMemo(() => {
    if (order.guest_total === 0) {
      return true;
    }

    return false;
  }, [order.guest_total]);

  const validateNamesAndPhones = () => {
    let valid = true;
    const newErrors = {
      [ECheckoutFieldTypes.FIRST_NAME]: '',
      [ECheckoutFieldTypes.LAST_NAME]: '',
      [ECheckoutFieldTypes.PHONE_NUMBER]: '',
      [ECheckoutFieldTypes.EMAIL]: '',
    };

    if (_.isEmpty(firstName.trim()) || firstName.trim() === '') {
      newErrors[ECheckoutFieldTypes.FIRST_NAME] = 'First name is required.';
      valid = false;
    }

    if (_.isEmpty(lastName.trim()) || lastName.trim() === '') {
      newErrors[ECheckoutFieldTypes.LAST_NAME] = 'Last name is required.';
      valid = false;
    }

    if (!phoneNumber.match(phoneRegExp)) {
      newErrors[ECheckoutFieldTypes.PHONE_NUMBER] = 'Enter a valid phone number.';
      valid = false;
    }

    if (_.isEmpty(email)) {
      newErrors[ECheckoutFieldTypes.EMAIL] = 'Enter a valid email.';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const confirmStripeSetup = async () => {
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${getBaseUrl()}${reverse({
          url: URLS.CHECKOUT_DETAILS,
          params: { storeId, orderId: order.id },
        })}`,
      },
    });

    if (result.error) {
      setIsSubmitting(false);
      message.error(result.error.message);
    }
  };

  const handleCompleteZeroPurchase = useCallback(() => {
    completeZeroPurchase({
      orderId: order.id,
      email,
    })
      .then(() => {
        history.push(
          reverse({
            url: URLS.CHECKOUT_DETAILS,
            params: { storeId, orderId: order.id },
          }),
        );
      })
      .catch((error) => {
        setIsSubmitting(false);
        message.error(error.message);
      });
  }, [email, order, storeId]);

  const handleChangeEmailField = useCallback((event) => {
    setEmail(event.complete ? event.value.email : '');
  }, []);

  const handleSaveCardData = async (event) => {
    setIsSubmitting(true);
    event.preventDefault();

    // Reset error messages
    setErrors({
      [ECheckoutFieldTypes.FIRST_NAME]: '',
      [ECheckoutFieldTypes.LAST_NAME]: '',
      [ECheckoutFieldTypes.PHONE_NUMBER]: '',
      [ECheckoutFieldTypes.EMAIL]: '',
    });

    if (!validateNamesAndPhones()) {
      scrollToTop();
      setIsSubmitting(false);
      return;
    }

    // Check if Sripe library and stripe elements are loaded
    if (!stripe || !elements) {
      setIsSubmitting(false);
      return;
    }

    updateCustomer({
      orderId: order.id,
      customerId,
      firstName,
      lastName,
      phone: phoneNumber,
    })
      .then(() => {
        setIsSubmitting(false);
        capture('checkout_submitted', {
          store_id: storeId,
          order_id: order.id,
          order_total: order.guest_total,
          is_zero_amount: orderIsForZeroAmount,
        });

        if (orderIsForZeroAmount) {
          handleCompleteZeroPurchase();
        } else {
          confirmStripeSetup();
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        message.error(error.message);
      });
  };

  const handleChangeField = useCallback(({ field, value }: { field: string; value: string }) => {
    switch (field) {
      case ECheckoutFieldTypes.FIRST_NAME:
        setFirstName(value);
        break;
      case ECheckoutFieldTypes.LAST_NAME:
        setLastName(value);
        break;
      case ECheckoutFieldTypes.PHONE_NUMBER:
        setPhoneNumber(value);
        break;
      default:
        break;
    }
  }, []);

  return (
    <Card style={{ padding: '20px' }}>
      <form onSubmit={handleSaveCardData} className="StoreCheckout-form">
        <Typography variant="h6" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
          Checkout
        </Typography>
        <div className="StoreCheckout-form_mobile">
          <Row gutter={[16, 16]}>
            <Col sm={12} xs={24} order={1}>
              <TextField
                required
                fullWidth
                label="First name"
                onChange={_.debounce(
                  (event) =>
                    handleChangeField({
                      field: ECheckoutFieldTypes.FIRST_NAME,
                      value: event.target.value,
                    }),
                  500,
                )}
                showHelperIcon={false}
                error={!_.isEmpty(errors[ECheckoutFieldTypes.FIRST_NAME])}
                errorMessage={
                  errors[ECheckoutFieldTypes.FIRST_NAME] && (
                    <p className="error">{errors[ECheckoutFieldTypes.FIRST_NAME]}</p>
                  )
                }
              />
            </Col>
            <Col sm={12} xs={24} order={2}>
              <TextField
                required
                fullWidth
                label="Last name"
                onChange={_.debounce(
                  (event) =>
                    handleChangeField({
                      field: ECheckoutFieldTypes.LAST_NAME,
                      value: event.target.value,
                    }),
                  500,
                )}
                showHelperIcon={false}
                error={!_.isEmpty(errors[ECheckoutFieldTypes.LAST_NAME])}
                errorMessage={
                  errors[ECheckoutFieldTypes.LAST_NAME] && (
                    <p className="error">{errors[ECheckoutFieldTypes.LAST_NAME]}</p>
                  )
                }
              />
            </Col>
            <Col sm={{ span: 12, order: 3 }} xs={{ span: 24, order: 4 }}>
              <FormControl
                label="Buyer email"
                required
                fullWidth
                showHelperIcon={false}
                error={!_.isEmpty(errors[ECheckoutFieldTypes.EMAIL])}
                errorMessage={
                  errors[ECheckoutFieldTypes.EMAIL] && (
                    <p className="error">{errors[ECheckoutFieldTypes.EMAIL]}</p>
                  )
                }
              >
                <LinkAuthenticationElement onChange={handleChangeEmailField} />
              </FormControl>
            </Col>
            <Col sm={{ span: 12, order: 4 }} xs={{ span: 24, order: 3 }}>
              <TextField
                required
                fullWidth
                label="Buyer phone number"
                helperText="By providing your number, you agree to order and support calls from The Host Co. No marketing."
                onChange={_.debounce(
                  (event) =>
                    handleChangeField({
                      field: ECheckoutFieldTypes.PHONE_NUMBER,
                      value: event.target.value,
                    }),
                  500,
                )}
                showHelperIcon={false}
                error={!_.isEmpty(errors[ECheckoutFieldTypes.PHONE_NUMBER])}
                errorMessage={
                  errors[ECheckoutFieldTypes.PHONE_NUMBER] && (
                    <p className="error">{errors[ECheckoutFieldTypes.PHONE_NUMBER]}</p>
                  )
                }
              />
            </Col>
            {!orderIsForZeroAmount && (
              <Col span={24} order={5}>
                <PaymentElement
                  options={{
                    layout: {
                      type: 'accordion',
                      defaultCollapsed: true, // This hides the card fields by default
                      radios: 'always', // Highly recommended for the collapsed look
                      spacedAccordionItems: true, // Optional add spacing between the options
                    },
                    fields: {
                      billingDetails: 'auto',
                    },
                  }}
                />
              </Col>
            )}
          </Row>
          <Agreement />
        </div>

        <div className="StoreCheckout-fix-actions">
          <div className="StoreCheckout-fix-actions-content">
            <Button variant="contained" type="submit" disabled={isSubmitting}>
              Place your order
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default CheckoutForm;
